import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeftRight, Loader2 } from 'lucide-react';
import { useApi } from '../../lib/api.js';

// =============================================================================
// ReturnBookingModal — Slice 7d.duplicate
// =============================================================================
// Single-date picker for creating a Return booking. Pickup/dropoff are swapped,
// stops are dropped, ref gains R prefix. Allocation, payment, Stripe state all
// reset like normal duplicate.
// =============================================================================

export default function ReturnBookingModal({ booking, onClose }) {
    const navigate = useNavigate();
    const api = useApi();
    const qc = useQueryClient();

    const sourcePickupIso = String(booking.pickup_date || '').slice(0, 10);

    const defaultDate = useMemo(() => {
        const [y, m, d] = sourcePickupIso.split('-').map(Number);
        if (!y || !m || !d) return new Date().toISOString().slice(0, 10);
        const base = new Date(y, m - 1, d);
        base.setDate(base.getDate() + 1);
        return base.toISOString().slice(0, 10);
    }, [sourcePickupIso]);

    const todayIso = new Date().toISOString().slice(0, 10);

    const [returnDate, setReturnDate] = useState(defaultDate);
    const [copyFlightDetails, setCopyFlightDetails] = useState(false);
    const [copyInternalNotes, setCopyInternalNotes] = useState(false);
        const [copyPaxComments, setCopyPaxComments] = useState(false);
    const [copyChauffeurNotes, setCopyChauffeurNotes] = useState(false);
    const [error, setError] = useState(null);

    const hasFlightDetails = !!(booking.flight_number || booking.tail_number || booking.fbo_code);
    const hasInternalNotes = !!(booking.internal_notes && booking.internal_notes.trim());
       const hasPaxComments = !!(booking.pax_comments && booking.pax_comments.trim());
    const hasChauffeurNotes = !!(booking.chauffeur_notes && booking.chauffeur_notes.trim());

    const mutation = useMutation({
        mutationFn: async () => api.post(`/bookings/${booking.id}/duplicate`, {
            target_dates: [returnDate],
            reverse: true,
            copy_flight_details: copyFlightDetails,
            copy_internal_notes: copyInternalNotes,
                        copy_pax_comments: copyPaxComments,
            copy_chauffeur_notes: copyChauffeurNotes,
        }),
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ['bookings'] });
            qc.invalidateQueries({ queryKey: ['bookings', 'list'] });
            const newBooking = data.bookings[0];
            onClose();
            navigate(`/bookings/${newBooking.id}`);
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to create return booking';
            setError(msg);
        },
    });

    return (
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget && !mutation.isPending) onClose();
            }}
        >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg my-8">
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <div className="flex items-center gap-2.5">
                        <ArrowLeftRight className="w-5 h-5 text-emerald-600" />
                        <h2 className="text-lg font-semibold text-ink">Create return booking</h2>
                    </div>
                    <button
                        onClick={() => onClose()}
                        disabled={mutation.isPending}
                        className="p-1.5 hover:bg-slate-100 rounded transition disabled:opacity-50"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    <div className="rounded-md bg-slate-50 border border-slate-200 px-3.5 py-3 text-sm space-y-2">
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                            Return itinerary
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-start gap-2">
                                <span className="text-xs text-ink-subtle uppercase tracking-wide w-12 mt-0.5">From</span>
                                <span className="text-ink flex-1">{booking.dropoff_address}</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-xs text-ink-subtle uppercase tracking-wide w-12 mt-0.5">To</span>
                                <span className="text-ink flex-1">{booking.pickup_address}</span>
                            </div>
                        </div>
                        <div className="text-xs text-ink-subtle pt-1 border-t border-slate-200">
                            Ref will be <span className="font-mono">R{booking.booking_ref_master}</span>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1.5 block">
                            Return date
                        </label>
                        <input
                            type="date"
                            value={returnDate}
                            min={todayIso}
                            onChange={(e) => setReturnDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                    </div>

                    <div className="text-xs text-ink-subtle">
                        Pickup time will inherit <span className="font-mono text-ink">{booking.pickup_time?.slice(0, 5)}</span>
                        {' '}from the source booking. Edit it on the new booking after creation if needed.
                    </div>

                                        {(hasFlightDetails || hasInternalNotes || hasPaxComments || hasChauffeurNotes) && (
                        <div>
                            <label className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2 block">
                                Carry over (optional)
                            </label>
                            <div className="space-y-2">
                                {hasFlightDetails && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyFlightDetails} onChange={(e) => setCopyFlightDetails(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm">
                                            <div className="text-ink">Flight details</div>
                                            <div className="text-xs text-ink-subtle">Returns usually pick up from arrivals, not departures</div>
                                        </div>
                                    </label>
                                )}
                                {hasInternalNotes && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyInternalNotes} onChange={(e) => setCopyInternalNotes(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm text-ink">Internal notes</div>
                                    </label>
                                )}
                                {hasPaxComments && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyPaxComments} onChange={(e) => setCopyPaxComments(e.target.checked)} className="mt-0.5" />
                                                                                <div className="text-sm text-ink">Booking notes</div>
                                    </label>
                                )}
                                {hasChauffeurNotes && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyChauffeurNotes} onChange={(e) => setCopyChauffeurNotes(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm text-ink">Chauffeur notes</div>
                                    </label>
                                )}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50 rounded-b-lg">
                    <button
                        type="button"
                        onClick={() => onClose()}
                        disabled={mutation.isPending}
                        className="px-3.5 py-2 text-sm text-ink-subtle hover:text-ink transition disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => mutation.mutate()}
                        disabled={!returnDate || mutation.isPending}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded transition"
                    >
                        {mutation.isPending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Creating…
                            </>
                        ) : (
                            <>
                                <ArrowLeftRight className="w-4 h-4" />
                                Create return
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
